// Session-replay collector (rrweb).
//
// Once GET /v1/config and the consent gate have settled, decides whether to
// record this session and in which mode, then lazy-loads the rrweb recorder
// bundle and drives it. See docs/session-replay.md §2.
//
//   continuous  the session is in the always-on cohort — record and upload
//               chunks live.
//   buffered    the session is in the error-triggered cohort — record into a
//               capped in-memory ring buffer and upload it only once a JS
//               error occurs, then carry on live.
//   off         the session is in neither cohort — rrweb is never loaded.
//
// rrweb is NOT bundled here: it ships as a separate artifact
// (ambilobe-rum-replay.min.js) injected on demand, so its ~30 KB never reaches
// the core loader for the sessions that don't record.

import { uuid, safely } from '../safe.js';
import { getIdentity } from '../identity.js';
import { shouldEmit, onConsentChange } from '../consent.js';
import { isReplayEnabled, inSessionTraceCohort, inErrorReplayCohort, onConfigLoaded } from '../config.js';
import { initReplayTransport, pushReplayEvent, pushReplayBatch } from '../replay-transport.js';

const RRWEB_GLOBAL = '__ambRrwebRecord';
const RRWEB_FULL_SNAPSHOT = 2;          // rrweb EventType.FullSnapshot
// Continuous: a periodic full snapshot every 10 min as scrub insurance.
// Buffered: a tight checkout so the in-memory ring buffer stays bounded — it
// is reset to the latest checkout segment on every checkout.
const CHECKOUT_CONTINUOUS_MS = 10 * 60 * 1000;
const CHECKOUT_BUFFERED_MS = 30 * 1000;

let _cfg = null;
let _decided = false;
let _mode = 'off';
let _ring = [];                         // buffered-mode in-memory event ring

export function initReplay(cfg) {
  _cfg = cfg || {};
  // Replay only ever turns on via the GET /v1/config `replay` block, which
  // loads async — re-evaluate when the config settles and when consent moves.
  onConfigLoaded(decide);
  onConsentChange(decide);
}

function decide() {
  if (_decided) return;
  if (!isReplayEnabled()) return;         // app/receiver have not enabled replay
  if (!shouldEmit()) return;              // consent not granted — onConsentChange retries

  const mode = inSessionTraceCohort() ? 'continuous'
    : inErrorReplayCohort() ? 'buffered'
      : 'off';
  if (mode === 'off') { _decided = true; return; }   // not sampled — never record

  const urls = resolveUrls();
  if (!urls) { _decided = true; return; }            // endpoint isn't a /v1/events URL

  _decided = true;
  _mode = mode;
  startRecording(urls);
}

// Derive the replay endpoint and the rrweb-bundle URL from the beacon
// endpoint (…/v1/events → …/v1/replay/{app}, …/v1/rum/ambilobe-rum-replay.min.js).
function resolveUrls() {
  const endpoint = _cfg.endpoint;
  if (typeof endpoint !== 'string') return null;
  const i = endpoint.indexOf('/v1/events');
  if (i < 0) return null;
  const base = endpoint.slice(0, i);
  const replayUrl = base + '/v1/replay/' + encodeURIComponent(_cfg.appId);
  return {
    replayUrl: replayUrl,
    beaconUrl: _cfg.token ? replayUrl + '/' + encodeURIComponent(_cfg.token) : replayUrl,
    scriptUrl: _cfg.replayScriptUrl || (base + '/v1/rum/ambilobe-rum-replay.min.js')
  };
}

function startRecording(urls) {
  const id = getIdentity();
  if (!id) return;

  initReplayTransport({
    replayUrl: urls.replayUrl,
    beaconUrl: urls.beaconUrl,
    token: _cfg.token || null,
    sessionId: id.sessionId,
    recordingId: uuid(),                 // one recording per page load
    environment: _cfg.environment || 'production'
  });

  loadRrweb(urls.scriptUrl, (record) => {
    if (typeof record !== 'function') return;
    // rrweb keeps recording for the life of the page (across SPA routes); we
    // never stop it mid-page, so its stop() handle is intentionally dropped.
    safely(() => record(buildRrwebConfig()));
    if (_mode === 'buffered') armErrorTrigger();
  });
}

function buildRrwebConfig() {
  const p = _cfg.privacy || {};
  return {
    emit: onRrwebEvent,
    // Privacy-first defaults: every input value is masked (password inputs are
    // masked by rrweb regardless). Hosts opt nodes in/out via the documented
    // .amb-block / .amb-mask classes and data-amb-* attributes.
    maskAllInputs: p.maskAllInputs !== false,
    blockClass: p.blockClass || 'amb-block',
    blockSelector: p.blockSelector || '[data-amb-block]',
    maskTextClass: p.maskTextClass || 'amb-mask',
    maskTextSelector: p.maskTextSelector || '[data-amb-mask]',
    ignoreClass: p.ignoreClass || 'amb-ignore',
    inlineStylesheet: true,              // replays render even if the CSS host is gone
    recordCanvas: false,                 // expensive — opt-in only
    collectFonts: false,
    checkoutEveryNms: _mode === 'buffered' ? CHECKOUT_BUFFERED_MS : CHECKOUT_CONTINUOUS_MS
  };
}

function onRrwebEvent(event, isCheckout) {
  if (_mode === 'continuous') {
    pushReplayEvent(event);
    return;
  }
  // buffered: keep only events since the last checkout, so the ring stays
  // bounded and always opens with a full snapshot.
  if (isCheckout) _ring = [];
  _ring.push(event);
}

// Buffered mode: the first JS error promotes the session to live capture and
// flushes the buffered window as the recording's opening, error-flagged chunk.
function armErrorTrigger() {
  const trip = () => promote();
  window.addEventListener('error', trip, true);
  window.addEventListener('unhandledrejection', trip, true);
}

function promote() {
  if (_mode !== 'buffered') return;      // already promoted
  _mode = 'continuous';
  const buffered = _ring;
  _ring = [];
  pushReplayBatch(buffered, { err: true });
}

function loadRrweb(scriptUrl, cb) {
  if (window[RRWEB_GLOBAL]) { cb(window[RRWEB_GLOBAL]); return; }
  try {
    const s = document.createElement('script');
    s.src = scriptUrl;
    s.async = true;
    if (_cfg.nonce) s.nonce = _cfg.nonce;
    s.addEventListener('load', () => cb(window[RRWEB_GLOBAL] || null));
    s.addEventListener('error', () => cb(null));
    (document.head || document.documentElement).appendChild(s);
  } catch (_) {
    cb(null);
  }
}
